import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnOptionClick } from '@philips-emr/hpms-provider-accreditation-components';
import { ButtonAPI, FetchAPI, FieldAPI, FormAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnChange, OnClick, OnMount, OnValidate } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import './provider-accreditation-beds-controller.css';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ProviderAccreditationBedsAliases as Aliases } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationSteps } from './provider-accreditation-controller';

const localValidatorSUS = 'islowerOrEqualTheOtherValue';

type MyData = {
  nrSequence?: number,
  ieBeds?: string,
  dsBeds?: string,
  qtBed?: number,
  qtBedSUS?: number
};

interface BedsDomain {
  dsDominio: string,
  vlDominio: string
}

type MyException = {
  body: string,
};

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
};

@Controller({ fragment: 'provider-accreditation-beds' })
export default class ProviderAccreditationBedsController extends BaseController {

  @Inject(Aliases.FETCH_BED)
  private fetchBed: FetchAPI;
  @Inject(Aliases.FETCH_LIST_BEDS)
  private fetchListBeds: FetchAPI;
  @Inject(Aliases.FIELD_QUANTITY_SUS)
  private fieldQuantitySus: FieldAPI;
  @Inject(Aliases.FIELD_CARD)
  private fieldCards: FieldAPI;
  @Inject(Aliases.FORM_BEDS)
  private formBeds!: FormAPI;
  @Inject('save-action')
  private saveAction: ButtonAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;
  @Inject(Aliases.FIELD_BEDS)
  private fieldBeds: FieldAPI;
  @Inject(Aliases.FIELD_AMOUNT)
  private fieldAmount: FieldAPI;

  @OnMount(Aliases.LAYOUT_GENERAL_INFORMATION)
  onRootLayoutMount(): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqAccreditation: nrSeqAccreditation });
    this.fetchListBeds.forceFetch();
  }

  @OnMount(Aliases.FORM_PANEL)
  onMountPanelForm(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.BEDS);
  }

  @HPMSOnCreateMenu(Aliases.MY_DATA_CARD)
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { editable } = data;
    shouldRender(editable == 'S');
  }

  @OnMount(Aliases.FORM_BEDS)
  onMountForm(): void {
    this.fieldQuantitySus.addLocalValidator(localValidatorSUS, (quantitySUS) => {
      const quantity = this.formBeds.getRecord()['qtBeds'];
      const valid = (Number(quantitySUS ?? 0) <= Number(quantity ?? 0));
      return [
        {
          message: !valid ? 'pa.valid-amount-sus' : '',
          name: localValidatorSUS,
          valid,
        },
      ];
    });
  }

  @OnAfterFetch(Aliases.FETCH_LIST_BEDS)
  OnAfterFetchList(event: FetchEvent): void {
    const data = event.data as unknown as [];
    const isEmpty = isEmptyArrayOrObject(data);
    this.fieldCards.setVisible(!isEmpty);
  }

  @OnValidate(Aliases.FORM_BEDS)
  onValidateForm(event: ValidateEvent): void {
    const { valid } = event;
    this.saveAction.setEnabled(valid);
  }

  @OnChange(Aliases.FORM_BEDS)
  onChangeForm(): void {
    this.formBeds.validate();
  }

  @OnClick(Aliases.BUTTON_CONFIRM)
  async onclick(): Promise<void> {
    if(this.formBeds.validate()) {
      const record = this.formBeds.getRecord() as MyData;
      await this.saveRecord(record);
      this.formBeds.setRecord({});
      this.showValidations(false);
    } else {
      this.showValidations(true);
    }
  }

  @OnClick(Aliases.BUTTON_CLEAN)
  onCleanForm(): void {
    this.cleanForm();
    this.showValidations(false);
  }

  private showValidations(validations: boolean): void {
    this.fieldAmount.setState({ showValidations: validations });
    this.fieldBeds.setState({ showValidations: validations });
    this.fieldQuantitySus.setState({ showValidations: validations });
  }

  @HPMSOnOptionClick(Aliases.MY_DATA_CARD)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSequence } = data as MyData;
    const action = {
      [OptionKey.EDIT]: () => this.editRecord(Number(nrSequence)),
      [OptionKey.DELETE]: () => this.deleteRecord(Number(nrSequence)),
    };

    if (action[key]) {
      action[key]();
    }
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.BEDS);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, event);
    }
  }

  private cleanForm(): void {
    this.formBeds.setRecord({});
    this.formBeds.setData({});
  }

  private editRecord(nrSequence: number): void {
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    this.fetchBed.setParameters({ nrSeqUser: nrSeqUser, nrSeqAccreditation: nrSeqAccreditation, nrSequence: nrSequence });
    this.fetchBed.forceFetch();
  }

  private async saveRecord(record: MyData): Promise<void> {
    try {
      const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
      const { vlDominio } = record.ieBeds as unknown as BedsDomain;
      await HttpService.post(Endpoint.POST.BEDS, { nrSeqUser, nrSeqAccreditation }, { body: { ieBeds: vlDominio, ...record } });
      this.fieldCards.setVisible(true);
      this.fetchListBeds.forceFetch();
    } catch (e) {
      const { body } = e as MyException;
      ToastServiceProviderAccreditation.error(MessageService.translate(body));
    }
  }

  private async deleteRecord(nrSequence: number): Promise<void> {
    try {
      const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
      await HttpService.delete(Endpoint.DELETE.BEDS, { nrSeqUser, nrSeqAccreditation, nrSequence });
      this.cleanForm();
      this.fetchListBeds.forceFetch();
    } catch (e) {
      const { body } = e as MyException;
      ToastServiceProviderAccreditation.error(MessageService.translate(body));
    }
  }
}
